// @flow

import { completeRequest, beginRequest } from "@store/utils";
import type { DynamicQuestionsState } from "./types";
import type { Action } from "redux-action";
import type { ApprovableContainer } from "@modules/service/types";
import type { DynamicQuestionRevision } from "@modules/service/reducer/dynamic-questions/types";

export type ShowDynamicQuestionModalRequest = $Exact<{
  show: boolean,
  step?: number,
}>;

const startRequest = beginRequest(["requestsInProgress"]);

const finishRequest = completeRequest(["requestsInProgress"]);

export const onAddNewDynamicQuestion = (state: DynamicQuestionsState) => () => startRequest(state);
export const onAddNewDynamicQuestionSuccess = (state: DynamicQuestionsState) => () =>
  finishRequest(state);
export const onAddNewDynamicQuestionError = (state: DynamicQuestionsState) => () =>
  finishRequest(state);

export const onUpdateDynamicQuestion = (state: DynamicQuestionsState) => () => startRequest(state);
export const onUpdateDynamicQuestionSuccess = (state: DynamicQuestionsState) => () =>
  finishRequest(state);
export const onUpdateDynamicQuestionError = (state: DynamicQuestionsState) => () =>
  finishRequest(state);

export const onGetDynamicQuestions = (state: DynamicQuestionsState) => () => startRequest(state);

export const onGetDynamicQuestionsSuccess = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  list: payload,
});

export const onGetDynamicQuestionsError = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onNewDynamicQuestion = (state: DynamicQuestionsState) => (action: Action<void>) => ({
  ...state,
  draft: {
    questionText: null,
    questionType: null,
  },
});

export const onShowAddDynamicQuestionModal = (state: DynamicQuestionsState) => ({
  payload,
}: Action<ShowDynamicQuestionModalRequest>) => {
  if (!payload) {
    return state;
  }

  return {
    ...state,
    modals: {
      ...state.modals,
      showAddDynamicQuestionModal: payload.show,
      modalStep: payload.step || state.modals.modalStep,
    },
  };
};

export const onUpdateDynamicQuestionDraft = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  draft: payload,
});

export const onSetDynamicQuestionRevisionSchema = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  error: null,
  schema: payload,
});

export const onGetDynamicQuestionActiveSuccess = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  current: payload,
});

export const onGetDynamicQuestionActiveError = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onSetDynamicQuestionAssignments = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  assignments: payload,
});

export const onSetDynamicQuestionDraftAssignments = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  draftAssignments: payload,
});

export const onSetDynamicQuestionStatus = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  configuration: {
    ...state.configuration,
    status: payload,
  },
});

export const onGetDynamicQuestion = (state: DynamicQuestionsState) => ({ payload }: Object) =>
  startRequest(state);

export const onGetDynamicQuestionSuccess = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  current: payload,
});

export const onGetDynamicQuestionError = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onShowDynamicQuestionEditModal = (state: DynamicQuestionsState) => ({
  payload,
}: Action<ShowDynamicQuestionModalRequest>) => {
  if (!payload) {
    return state;
  }

  return {
    ...state,
    modals: {
      ...state.modals,
      showEditModal: payload.show,
      modalStep: payload.step || state.modals.modalStep,
    },
  };
};

export const onSetShowSubmitDynamicQuestionModal = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showSubmitModal: payload,
  },
});

export const onSetShowCancelDynamicQuestionModal = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelModal: payload,
  },
});

export const onSubmitDynamicQuestionForReviewError = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onCancelDynamicQuestionReviewError = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onStartDynamicQuestionDraftSuccess = (state: DynamicQuestionsState) => (
  action: Action<ApprovableContainer<DynamicQuestionRevision>>,
) => ({
  ...state,
  draft: (action.payload && action.payload.active) || {},
});

export const onSetShowDiscardDynamicQuestionModal = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDiscardModal: payload,
  },
});

export const onSetShowCancelDiscardDynamicQuestionModal = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelDiscardModal: payload,
  },
});

export const onDuplicateDynamicQuestionSuccess = (state: DynamicQuestionsState) => (
  action: Action<DynamicQuestionRevision>,
) => ({
  ...state,
  draft: {
    questionText: null,
    questionType: (action.payload || {}).questionType,
    isMandatory: (action.payload || {}).isMandatory,
    advisorType: (action.payload || {}).advisorType,
    consentToShare: (action.payload || {}).consentToShare,
    startNotification: (action.payload || {}).startNotification,
    updateNotification: (action.payload || {}).updateNotification,
    closeNotification: (action.payload || {}).closeNotification,
    absenceReasons: (action.payload || {}).absenceReasons,
    durationDays: (action.payload || {}).durationDays,
    dayType: (action.payload || {}).dayType,
    selectableValues: (action.payload || {}).selectableValues,
  },
});

export const onSetShowDisableDynamicQuestionModal = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDisableModal: payload,
  },
});

export const onStartDynamicQuestionDraftError = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDiscardDynamicQuestionDraftError = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDuplicateDynamicQuestionError = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onCancelDynamicQuestionReviewAndDiscardError = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDisableDynamicQuestionError = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onUpdateDynamicQuestionAssignmentsError = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onShowEditDynamicQuestionAssignmentsModal = (state: DynamicQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showEditAssignmentsModal: payload,
  },
});
